import { z } from 'zod'

import {
  getCurrentNavbarApp,
  getNavbarAppHref,
} from '../../../components/navbar/Navbar'

export interface WorkspaceEncounterHrefOptions {
  encounterId: string
  orgSlug: string
}

export interface WorkspaceEncounterListHrefOptions {
  orgSlug: string
}

export function getWorkspaceHref(path: string) {
  const currentApp = getCurrentNavbarApp()
  if (currentApp === 'workspace' || currentApp === 'bpo') {
    return path
  }

  return getNavbarAppHref({ app: 'workspace', path })
}

export function getWorkspaceLogicDiagramHref({
  nodeId,
  slug,
}: {
  nodeId?: string
  slug: string
}) {
  const encodedSlug = encodeURIComponent(slug)
  if (nodeId) {
    const searchParams = new URLSearchParams({ node: nodeId })
    return getWorkspaceHref(`/logic/${encodedSlug}?${searchParams.toString()}`)
  }

  return getWorkspaceHref(`/logic/${encodedSlug}`)
}

export interface WorkspaceCredentialResetsHrefOptions {
  conversationId: string | null
  orgSlug: string
}

export function getWorkspaceCredentialResetsHref({
  conversationId,
  orgSlug,
}: WorkspaceCredentialResetsHrefOptions) {
  const searchParams = new URLSearchParams()
  if (conversationId !== null) {
    searchParams.set('conversationId', conversationId)
  }

  const query = searchParams.toString()
  const path =
    query === ''
      ? `/${orgSlug}/credentials/resets`
      : `/${orgSlug}/credentials/resets?${query}`
  if (getCurrentNavbarApp() === 'workspace') {
    return path
  }

  return getNavbarAppHref({ app: 'workspace', path })
}

export interface WorkspaceKnowledgeMemoryHrefOptions {
  accountId: string | null
  agent: string
  orgSlug: string
  title: string
}

const WorkspaceKnowledgeMemoryOpenValueSchema = z.object({
  accountId: z.uuid().nullable(),
  agent: z.string().min(1),
  title: z.string().min(1),
})

export type WorkspaceKnowledgeMemoryOpenValue = z.infer<
  typeof WorkspaceKnowledgeMemoryOpenValueSchema
>

export function serializeWorkspaceKnowledgeMemoryOpenValue({
  accountId,
  agent,
  title,
}: WorkspaceKnowledgeMemoryOpenValue) {
  return `${agent}|${accountId ?? 'global'}|${title}`
}

export function parseWorkspaceKnowledgeMemoryOpenValue(value: string) {
  const firstSeparatorIndex = value.indexOf('|')
  if (firstSeparatorIndex === -1) {
    return null
  }

  const secondSeparatorIndex = value.indexOf('|', firstSeparatorIndex + 1)
  if (secondSeparatorIndex === -1) {
    return null
  }

  const agent = value.slice(0, firstSeparatorIndex)
  const accountKey = value.slice(firstSeparatorIndex + 1, secondSeparatorIndex)
  const title = value.slice(secondSeparatorIndex + 1)
  if (agent && title) {
    const parsed = WorkspaceKnowledgeMemoryOpenValueSchema.safeParse({
      accountId: accountKey === 'global' ? null : accountKey,
      agent,
      title,
    })
    return parsed.success ? parsed.data : null
  }

  return null
}

export function getWorkspaceKnowledgeMemoryHref({
  accountId,
  agent,
  orgSlug,
  title,
}: WorkspaceKnowledgeMemoryHrefOptions) {
  const searchParams = new URLSearchParams({
    account_scope: accountId ? 'account' : 'global',
    open: serializeWorkspaceKnowledgeMemoryOpenValue({
      accountId,
      agent,
      title,
    }),
    page: '1',
    query: title,
  })
  return getWorkspaceHref(`/${orgSlug}/knowledge?${searchParams.toString()}`)
}

export function getWorkspaceEncounterHref({
  encounterId,
  orgSlug,
}: WorkspaceEncounterHrefOptions) {
  const path = `/${orgSlug}/${encounterId}`
  return getWorkspaceHref(path)
}

export function getWorkspaceEncounterListHref({
  orgSlug,
}: WorkspaceEncounterListHrefOptions) {
  const path = `/${orgSlug}/encounters`
  return getWorkspaceHref(path)
}
